import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import {
  getAllTemplateDetails,
  getAllFeedbacksCategory,
  getAllFeedbackQuestionOptions,
  saveFeedbackAnswer,
} from "../../pages/assessment/components/feedbacks/feedbackAssessmentService";

import { questionService } from "../../service/assessment/questionService";

const ExamFeedbackContext = createContext(null);

const formatToLocalTime = (utcDateStr) => {
  if (!utcDateStr) return "Unlimited";

  const date = new Date(utcDateStr);
  const colomboTime = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);

  return colomboTime.toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const parseDDMMYYYY = (value) => {
  if (!value || value === "Unlimited") return null;

  const [datePart, timePart] = String(value).split(", ");
  if (!datePart || !timePart) return null;

  const [dd, mm, yyyy] = datePart.split("/");
  const [time, meridian] = timePart.split(" ");

  return new Date(`${yyyy}-${mm}-${dd} ${time} ${meridian}`);
};

const calculateDuration = (startTime, endTime) => {
  const start = parseDDMMYYYY(startTime);
  const end = parseDDMMYYYY(endTime);

  if (!start || !end) return "N/A";
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "Invalid date";

  const diffMs = Math.max(0, end - start);
  const days = Math.floor(diffMs / (24 * 3600000));
  const hours = Math.floor((diffMs % (24 * 3600000)) / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);

  return `${days} day ${hours} hr ${minutes} min ${seconds} sec`;
};

const isOtherOption = (label = "") => {
  const value = label.trim().toLowerCase();
  return value === "other" || value.includes("other") || value.includes("වෙනත්") || value.includes("பிற");
};

export function ExamFeedbackProvider({
  children,
  assessmentID,
  candidate_id,
  examSummary = null,
}) {
  const [templateData, setTemplateData] = useState(null);
  const [feedbackCategory, setFeedbackCategory] = useState([]);
  const [questionOptions, setQuestionOptions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [userId, setUserId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState("");

  const [summary, setSummary] = useState({
    assessment_title: "",
    subject: "",
    time_duration: null,
    questionCount: 0,
    answeredCount: 0,
    examStartTime: null,
    examFinishTime: null,
  });

  useEffect(() => {
    let cancelled = false;

    async function loadFeedbackData() {
      try {
        setLoading(true);
        setError("");

        const languageId = sessionStorage.getItem("studentMedium") || 1;
        const currentUserId = sessionStorage.getItem("user_id") || 0;
        setUserId(currentUserId);

        const baseRequests = [
          getAllTemplateDetails(),
          getAllFeedbacksCategory(),
          getAllFeedbackQuestionOptions(),
        ];

        const needsFallbackSummary = !examSummary;

        const requests = needsFallbackSummary
          ? [
              ...baseRequests,
              questionService.getAssessmentQuestions(assessmentID),
              questionService.getAssessmentAnswers(candidate_id, assessmentID),
            ]
          : baseRequests;

        const [templateRes, categoryRes, optionRes, questionRes, answerRes] =
          await Promise.all(requests);

        if (cancelled) return;

        const selectedTemplate =
          templateRes?.data?.find(
            (template) => String(template.language_id) === String(languageId)
          ) || null;

        setTemplateData(selectedTemplate);
        setFeedbackCategory(categoryRes?.data || []);
        setQuestionOptions(optionRes?.data || []);

        if (examSummary) {
          setSummary({
            assessment_title: examSummary.assessment_title || "",
            subject: examSummary.subject || "",
            time_duration: examSummary.time_duration || null,
            questionCount: examSummary.questionCount || 0,
            answeredCount: examSummary.answeredCount || 0,
            examStartTime: formatToLocalTime(examSummary.examStartTime),
            examFinishTime: formatToLocalTime(examSummary.examFinishTime),
          });
        } else {
          const parsedQuestions = JSON.parse(questionRes?.data?.examQuestions || "{}");
          const reportDetails = answerRes?.data?.candidate_grade_report_details || {};

          setSummary({
            assessment_title:
              questionRes?.data?.assessment_details?.title ||
              answerRes?.data?.candidateInfo?.assessment_title ||
              "",
            subject: questionRes?.data?.assessment_details?.subject || "",
            time_duration: questionRes?.data?.assessment_details?.time_duration || null,
            questionCount: parsedQuestions?.questions?.length || 0,
            answeredCount: answerRes?.data?.answers || 0,
            examStartTime: formatToLocalTime(reportDetails.attempt_start_time),
            examFinishTime: formatToLocalTime(reportDetails.attempt_end_time),
          });
        }
      } catch (err) {
        console.error("Error fetching feedback data:", err);
        if (!cancelled) setError("Failed to load feedback form.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (assessmentID && candidate_id) {
      loadFeedbackData();
    }
    return () => {
      cancelled = true;
    };
  }, [assessmentID, candidate_id, examSummary]);

  const categoryMap = useMemo(() => {
    return feedbackCategory.reduce((map, category) => {
      map[category.fb_qus_cat_id] = category.name;
      return map;
    }, {});
  }, [feedbackCategory]);

  const optionMap = useMemo(() => {
    return questionOptions.reduce((map, item) => {
      map[item.qus_id] = item.answer_options || [];
      return map;
    }, {});
  }, [questionOptions]);

  const getCategoryById = (id) => categoryMap[id];

  const getOptionsByQuestionId = (qusId) => optionMap[qusId] || [];

  const handleAnswerChange = (qusId, value) => {
    setAnswers((prev) => ({ ...prev, [qusId]: value }));
  };

  const handleCheckboxChange = (qusId, optionId) => {
    setAnswers((prev) => {
      const previousSelected = prev[qusId]?.selected || [];
      const selected = previousSelected.includes(optionId)
        ? previousSelected.filter((id) => id !== optionId)
        : [...previousSelected, optionId];

      return {
        ...prev,
        [qusId]: {
          selected,
          otherText: prev[qusId]?.otherText || "",
        },
      };
    });
  };

  const buildPayload = () => {
    const payload = {
      template_id: templateData.template_id,
      assessment_id: assessmentID,
      user_id: userId,
      status_id: 1,
      answers: [],
    };

    templateData.sections.forEach((section) => {
      section.questions.forEach((question) => {
        const category = getCategoryById(question.fb_qus_cat_id);
        const answer = answers[question.qus_id];
        if (!answer) return;

        if (category === "text") {
          payload.answers.push({
            qus_id: question.qus_id,
            fb_ans_text: answer,
          });
          return;
        }

        if (category === "rating") {
          payload.answers.push({
            qus_id: question.qus_id,
            fb_qus_ans_opt_id: answer,
          });
          return;
        }

        if (category === "choice_single" || category === "yes_no") {
          const selectedOption = getOptionsByQuestionId(question.qus_id).find(
            (opt) => opt.fb_qus_ans_opt_id === answer.selected
          );

          payload.answers.push({
            qus_id: question.qus_id,
            fb_qus_ans_opt_id: answer.selected,
            ...(isOtherOption(selectedOption?.answer_option) && answer.otherText?.trim()
              ? { fb_ans_text: answer.otherText.trim() }
              : {}),
          });
          return;
        }

        if (category === "choice_multi") {
          (answer.selected || []).forEach((optionId) => {
            const selectedOption = getOptionsByQuestionId(question.qus_id).find(
              (opt) => opt.fb_qus_ans_opt_id === optionId
            );

            payload.answers.push({
              qus_id: question.qus_id,
              fb_qus_ans_opt_id: optionId,
              ...(isOtherOption(selectedOption?.answer_option) && answer.otherText?.trim()
                ? { fb_ans_text: answer.otherText.trim() }
                : {}),
            });
          });
        }
      });
    });

    return payload;
  };

  const submitFeedback = async () => {
    if (!templateData) return false;

    setSubmitLoading(true);
    try {
      await saveFeedbackAnswer(buildPayload());
      return true;
    } catch (err) {
      console.error("Error saving feedback:", err);
      setError("Failed to save feedback.");
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <ExamFeedbackContext.Provider
      value={{
        templateData,
        summary,
        answers,
        loading,
        submitLoading,
        error,
        calculateDuration,
        isOtherOption,
        getCategoryById,
        getOptionsByQuestionId,
        handleAnswerChange,
        handleCheckboxChange,
        submitFeedback,
      }}
    >
      {children}
    </ExamFeedbackContext.Provider>
  );
}

export function useExamFeedback() {
  const context = useContext(ExamFeedbackContext);
  if (!context) {
    throw new Error("useExamFeedback must be used inside ExamFeedbackProvider");
  }
  return context;
}